import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Sparkles, Clock, CheckCircle2, Rocket } from "lucide-react";

const STATUS_META = {
  planned:     { label: "Geplant",       color: "#A3A3A3", icon: Clock },
  in_progress: { label: "In Arbeit",     color: "#F59E0B", icon: Rocket },
  launched:    { label: "Veröffentlicht", color: "#10B981", icon: CheckCircle2 },
};
const CATEGORY_META = {
  feature: { label: "Feature", color: "#00E5FF" },
  content: { label: "Content", color: "#A855F7" },
  other:   { label: "Sonstiges", color: "#737373" },
};

export default function RoadmapPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | feature | content | other

  useEffect(() => {
    api.get("/roadmap").then(({ data }) => setItems(data || [])).finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => (
    filter === "all" ? items : items.filter(i => i.category === filter)
  ), [items, filter]);

  const counts = useMemo(() => {
    const out = { all: items.length };
    for (const k of Object.keys(CATEGORY_META)) {
      out[k] = items.filter(i => i.category === k).length;
    }
    return out;
  }, [items]);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="roadmap-page">
      <div className="h-1 w-12 bg-[#00E5FF] mb-3" />
      <div className="eyebrow text-[#00E5FF] flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5" /> Roadmap
      </div>
      <h1 className="font-display font-black italic uppercase tracking-normal text-5xl sm:text-6xl leading-none mt-2">
        Bald da
      </h1>
      <p className="text-neutral-400 mt-4 max-w-2xl">
        Hier siehst du, woran wir gerade arbeiten und was in den nächsten Updates geplant ist.
        Features, Content, Verbesserungen — alles auf einen Blick.
      </p>

      {/* Kategorie-Filter */}
      <div className="flex flex-wrap gap-2 mt-8" data-testid="roadmap-filters">
        {[
          { k: "all", label: "Alle", color: "#FFFFFF" },
          ...Object.entries(CATEGORY_META).map(([k, m]) => ({ k, label: m.label, color: m.color })),
        ].map(({ k, label, color }) => {
          const active = filter === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k)}
              className="text-xs font-bold uppercase tracking-widest px-3 py-1.5 border transition-colors"
              style={active
                ? { borderColor: color, color, background: `${color}1A` }
                : { borderColor: "rgba(255,255,255,0.15)", color: "#a3a3a3" }}
              data-testid={`roadmap-filter-${k}`}
            >
              {label} <span className="text-[10px] opacity-70 ml-1">{counts[k] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {/* Liste */}
      <div className="mt-10 space-y-4" data-testid="roadmap-list">
        {loading && (
          <div className="text-neutral-500 text-sm italic">Lädt…</div>
        )}
        {!loading && visible.length === 0 && (
          <div className="card-surface p-8 text-center text-neutral-400">
            <Sparkles className="w-6 h-6 mx-auto text-[#00E5FF] mb-3" />
            <div className="font-display font-black italic uppercase text-lg">Bald mehr</div>
            <p className="text-sm mt-2">Aktuell sind keine Einträge in dieser Kategorie geplant. Schau bald wieder vorbei.</p>
          </div>
        )}
        {visible.map(it => {
          const stat = STATUS_META[it.status] || STATUS_META.planned;
          const cat = CATEGORY_META[it.category] || CATEGORY_META.feature;
          const Icon = stat.icon;
          return (
            <div
              key={it.id}
              className="card-surface p-0 overflow-hidden grid md:grid-cols-[200px,1fr]"
              data-testid={`roadmap-item-${it.id}`}
            >
              {/* Hero Bild oder Status-Tile */}
              <div
                className="relative h-32 md:h-auto min-h-[8rem] bg-[#0B0F19] overflow-hidden flex items-center justify-center"
                style={!it.hero_image ? { background: `linear-gradient(135deg, ${cat.color}22, transparent)` } : undefined}
              >
                {it.hero_image ? (
                  <img src={it.hero_image} alt={it.title} className="absolute inset-0 w-full h-full object-cover opacity-60" />
                ) : (
                  <Icon className="w-10 h-10" style={{ color: stat.color, opacity: 0.6 }} />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0F1419]/60 md:to-[#0F1419]/80" />
              </div>

              <div className="p-6 flex flex-col">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span
                    className="text-[10px] uppercase tracking-widest px-2 py-0.5 border"
                    style={{ borderColor: `${cat.color}66`, color: cat.color }}
                  >
                    {cat.label}
                  </span>
                  <span
                    className="text-[10px] uppercase tracking-widest px-2 py-0.5 border flex items-center gap-1"
                    style={{ borderColor: `${stat.color}66`, color: stat.color }}
                  >
                    <Icon className="w-3 h-3" /> {stat.label}
                  </span>
                  {it.target && (
                    <span className="text-[10px] uppercase tracking-widest text-neutral-500">
                      Ziel: <span className="text-neutral-300">{it.target}</span>
                    </span>
                  )}
                </div>
                <div className="font-display font-black italic uppercase text-2xl leading-tight">{it.title}</div>
                {it.description && (
                  <p className="text-sm text-neutral-400 mt-3 whitespace-pre-line leading-relaxed">{it.description}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
